import React, { useState } from 'react';
import { Modal, Form, Alert } from 'antd';
import { connect } from 'react-redux';
import { nullSafeGet } from '../../utils/DataAccessUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';
import OWButton from '../ow_button/OWButton';
import SupplierContactFields from '../request_to_add_supplier_form/SupplierContactFields';
import SupplierCoverageFields from '../request_to_add_supplier_form/SupplierCoverageFields';
import {
	buildSupplierPayload,
	getSupplierRequestInitialValues,
	formatMatchNames,
} from '../request_to_add_supplier_form/supplierRequestFormUtils';
import { approveEntityAddRequest } from '../../thunks/entity_add_requests_thunks';
import { fetchSuppliersForBuyers } from '../../thunks/supplier_private_networks_thunks';
import {
	problemTypesHeirarchicalForBuyer,
	problemTypesRestCrudThunksForBuyer,
} from '../../thunks/problem_types_thunks';
import {
	fetchLocationsWithRegionsForBuyer,
	locationsRestCrudThunksForBuyer,
} from '../../thunks/locations_thunks';

const CONTACT_STEP = 0;
const COVERAGE_STEP = 1;
const CONTACT_STEP_FIELDS = ['companyName', 'contactEmail', 'contactFirstName', 'contactLastName'];

// Redux collection the reused supplier search dispatches into; we only consume the resolved matches.
const DUPLICATE_SEARCH_COLLECTION = 'approveEntityAddRequestDuplicateSearch';

// Approver-facing materialization form, split into two steps (contact → coverage). Pre-filled
// from the request payload; the approver may override before the supplier is created.
function ApproveEntityAddRequestModal({
	visible,
	record,
	hideModal,
	onSuccess,
	problemTypes,
	locations,
	fetchProblemTypes,
	fetchMultipleProblemTypes,
	fetchLocations,
	fetchMultipleLocations,
	approveRequest,
	searchSuppliers,
}) {
	const [form] = Form.useForm();
	const [step, setStep] = useState(CONTACT_STEP);
	const [submitting, setSubmitting] = useState(false);
	const [errorMessage, setErrorMessage] = useState(undefined);
	// Non-blocking duplicate pre-check (same as the requester flow): `matches` holds any existing
	// suppliers found for the entered name+email; `warnedKey` records the values we last warned for,
	// so a second Continue click on the same values proceeds and lets the approver add a duplicate.
	const [duplicateMatches, setDuplicateMatches] = useState([]);
	const [duplicateWarnedKey, setDuplicateWarnedKey] = useState(undefined);
	const [checkingDuplicates, setCheckingDuplicates] = useState(false);

	const handleContinue = () => {
		setErrorMessage(undefined);
		form
			.validateFields(CONTACT_STEP_FIELDS)
			.then(() => {
				const values = form.getFieldsValue(true);
				const searchKey = `${values.companyName}|${values.contactEmail}`;
				// Already warned for these exact values → the approver chose to continue anyway.
				if (duplicateWarnedKey === searchKey) {
					setStep(COVERAGE_STEP);
					return;
				}
				setCheckingDuplicates(true);
				searchSuppliers(values.companyName, values.contactEmail)
					.then((matches) => {
						setCheckingDuplicates(false);
						if (matches && matches.length > 0) {
							setDuplicateMatches(matches);
							setDuplicateWarnedKey(searchKey);
						} else {
							setDuplicateMatches([]);
							setDuplicateWarnedKey(undefined);
							setStep(COVERAGE_STEP);
						}
					})
					.catch(() => {
						// A failed lookup must never block approval — just advance.
						setCheckingDuplicates(false);
						setStep(COVERAGE_STEP);
					});
			})
			.catch(() => {});
	};

	const handleAddSupplier = () => {
		setErrorMessage(undefined);
		form
			.validateFields()
			.then((values) => {
				setSubmitting(true);
				const payloadOverride = buildSupplierPayload(values);
				return approveRequest(nullSafeGet('id', record), payloadOverride)
					.then(() => {
						setSubmitting(false);
						onSuccess();
						hideModal();
					})
					.catch((error) => {
						setSubmitting(false);
						setErrorMessage(
							typeof error === 'string' ? error : 'Something went wrong approving this request.'
						);
					});
			})
			.catch(() => {});
	};

	const footer = (
		<div className="flex flex-row items-center justify-end" style={{ gap: '8px' }}>
			<OWButton type="default" onClick={hideModal}>
				Cancel
			</OWButton>
			{step === CONTACT_STEP ? (
				<OWButton
					type="primary"
					htmlType="button"
					onClick={handleContinue}
					loading={checkingDuplicates}
				>
					Continue
				</OWButton>
			) : (
				<OWButton type="primary" htmlType="button" onClick={handleAddSupplier} loading={submitting}>
					Add Supplier
				</OWButton>
			)}
		</div>
	);

	return (
		<Modal
			visible={visible}
			width={600}
			title="Add Supplier"
			onCancel={hideModal}
			closable={true}
			destroyOnClose={true}
			footer={footer}
		>
			<Form
				form={form}
				layout="vertical"
				initialValues={getSupplierRequestInitialValues(record, [])}
			>
				{errorMessage ? <Alert message={errorMessage} type="error" className="mb-4" /> : null}
				{step === CONTACT_STEP && duplicateMatches.length > 0 ? (
					<Alert
						type="warning"
						className="mb-4"
						showIcon
						message="This supplier may already exist"
						description={`A similar supplier is already on OpenWrench: ${formatMatchNames(
							duplicateMatches
						)}. Click Continue again to add it anyway.`}
					/>
				) : null}
				{/* Both steps stay mounted (hidden) so values + "Select All" watches persist. */}
				<div hidden={step !== CONTACT_STEP}>
					<SupplierContactFields />
				</div>
				<div hidden={step !== COVERAGE_STEP}>
					<SupplierCoverageFields
						form={form}
						problemTypes={problemTypes}
						fetchProblemTypes={fetchProblemTypes}
						fetchMultipleProblemTypes={fetchMultipleProblemTypes}
						locations={locations}
						fetchLocations={fetchLocations}
						fetchMultipleLocations={fetchMultipleLocations}
					/>
				</div>
			</Form>
		</Modal>
	);
}

const mapStateToProps = (state) => ({
	problemTypes: state.problem_types,
	locations: state.locations,
});

const mapDispatchToProps = (dispatch) => ({
	approveRequest: (id, payloadOverride) =>
		dispatch(approveEntityAddRequest(ROLE_TYPES.BUYER)(id, payloadOverride)),
	searchSuppliers: (name, email) =>
		dispatch(fetchSuppliersForBuyers(ROLE_TYPES.BUYER)(name, email)(DUPLICATE_SEARCH_COLLECTION)),
	fetchProblemTypes: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			problemTypesHeirarchicalForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleProblemTypes: (ids, targetCollectionName) =>
		dispatch(problemTypesRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
	fetchLocations: (params, targetCollectionName, pagination, sorting, filters, addTo) =>
		dispatch(
			fetchLocationsWithRegionsForBuyer(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addTo
			)
		),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForBuyer.readMultiple(ids, targetCollectionName)),
});

export default connect(mapStateToProps, mapDispatchToProps)(ApproveEntityAddRequestModal);
